import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { canAccessBoringBucket } from '../../lib/entitlements';
import '../Dashboard.css';

const APPLY_URL = 'https://theboringbucket.com';
const COMMUNITY_URL = 'https://community.theboringbucket.com';

/**
 * The Boring Bucket section home. Members see the member hub; everyone else
 * sees a marketing panel that routes them to apply at theboringbucket.com
 * (the program is application-gated — there is no public checkout).
 */
export const BoringBucket: React.FC = () => {
  const { user } = useAuth();
  const isMember = canAccessBoringBucket(user as any);

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content">
        <div style={{ padding: '1.5rem' }}>
          <div className="welcome-section">
            <h2>The Boring Bucket{' '}
              <span className="beta-badge" style={{ fontSize: 10, marginLeft: 6 }}>MENTORED</span>
            </h2>
            <p>
              {isMember
                ? 'Your mentored membership — the live account, the mentoring sessions, the framework, and the community.'
                : 'A mentored membership: watch a real-money account live, learn the framework, and run it alongside a small capped cohort.'}
            </p>
          </div>

          {isMember ? (
            <MemberHub />
          ) : (
            <NonMemberPanel />
          )}
        </div>
      </div>
    </div>
  );
};

const cardStyle: React.CSSProperties = {
  display: 'block',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10,
  padding: '20px 22px',
  textDecoration: 'none',
  color: 'inherit',
};

const MemberHub: React.FC = () => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 14,
  }}>
    <Link to="/boring-bucket/founder-trades" style={cardStyle}>
      <h3 style={{ margin: '0 0 6px', color: '#f9fafb', fontSize: '1.05em' }}>Founder's Trades</h3>
      <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>
        Live view of the real-money positions and recent closes — the trades you're following.
      </p>
    </Link>
    <Link to="/boring-bucket/mentoring" style={cardStyle}>
      <h3 style={{ margin: '0 0 6px', color: '#f9fafb', fontSize: '1.05em' }}>Mentoring</h3>
      <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>
        Upcoming collective mentoring sessions and the archive of past recordings.
      </p>
    </Link>
    <Link to="/boring-bucket/capital-plan" style={cardStyle}>
      <h3 style={{ margin: '0 0 6px', color: '#f9fafb', fontSize: '1.05em' }}>Capital Plan</h3>
      <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>
        The capital allocation framework — buying-power sizing, the 3-engine setup, profit handling.
      </p>
    </Link>
    <a href={COMMUNITY_URL} target="_blank" rel="noopener noreferrer" style={cardStyle}>
      <h3 style={{ margin: '0 0 6px', color: '#f9fafb', fontSize: '1.05em' }}>Community →</h3>
      <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>
        The members' forum — trade discussion, weekly recaps, and questions between sessions.
      </p>
    </a>
  </div>
);

const NonMemberPanel: React.FC = () => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(59,130,246,0.08) 100%)',
    border: '1px solid rgba(16,185,129,0.25)',
    borderRadius: 12,
    padding: '28px 26px',
  }}>
    <h3 style={{ margin: '0 0 10px', color: '#f9fafb', fontSize: '1.3em' }}>
      Get mentored — $299/mo
    </h3>
    <p style={{ color: '#cbd5e1', fontSize: 14, lineHeight: 1.7, margin: '0 0 14px', maxWidth: 620 }}>
      The Boring Bucket is a capped, mentored membership. You get the full platform,
      collective mentoring sessions, the community, and a live look at the real-money
      trades — so you can learn the framework by watching it run, then run it yourself.
    </p>
    <ul style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.9, margin: '0 0 18px', paddingLeft: 18 }}>
      <li>Live Founder's Trades — real positions, real P&amp;L, no curation</li>
      <li>Collective mentoring sessions, recorded</li>
      <li>The capital allocation framework and 3-engine setup</li>
      <li>Members' community</li>
    </ul>
    <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 16px' }}>
      Membership is application-only. Apply, and if it's a fit you'll get a setup link.
    </p>
    <a
      href={APPLY_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-block',
        background: '#10b981',
        color: '#0a0f1e',
        fontWeight: 700,
        fontSize: 14,
        padding: '11px 26px',
        borderRadius: 8,
        textDecoration: 'none',
      }}
    >
      Apply at theboringbucket.com →
    </a>
  </div>
);

export default BoringBucket;
